import React, { useState, useRef, useEffect, useContext } from 'react'
import { useReactMediaRecorder } from 'react-media-recorder'
import { useNavigate, useParams } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { createPortal } from 'react-dom'
import { ImportFiles } from '../dashboard/dialog/importFiles'
import { customFetch } from '@/utils/fetch'
import toast from 'react-hot-toast'

// Import Firebase storage
import { storage } from '../../firebase'
import { v4 as uuidv4 } from 'uuid'

// Icons
import { Mic, MicOff, Video, VideoOff, Pause, Play, Square, Save, AlertTriangle, Circle } from 'lucide-react'

interface RecordingPageProps {
  projectId?: string
}

const RecordingPage: React.FC<RecordingPageProps> = ({ projectId: propProjectId }) => {
  const navigate = useNavigate()
  const { projectId: paramProjectId } = useParams<{ projectId: string }>()
  const projectId = propProjectId || paramProjectId

  const [meetingTitle, setMeetingTitle] = useState('New Recording')
  const [showImportDialog, setShowImportDialog] = useState(false)
  const [recordedFile, setRecordedFile] = useState<File | null>(null)
  const [recordingDuration, setRecordingDuration] = useState(0)
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [browserCompatible, setBrowserCompatible] = useState(true)

  const videoPreviewRef = useRef<HTMLVideoElement>(null)

  // Add a fallback method for recording in case react-media-recorder is not working
  const [fallbackStream, setFallbackStream] = useState<MediaStream | null>(null)
  const [fallbackRecorder, setFallbackRecorder] = useState<MediaRecorder | null>(null)
  const [fallbackChunks, setFallbackChunks] = useState<Blob[]>([])
  const [usingFallback, setUsingFallback] = useState(false)

  // Add a live camera stream state
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null)

  // Check browser compatibility and request permissions
  useEffect(() => {
    // Check if MediaRecorder is supported
    if (!window.MediaRecorder) {
      console.error('MediaRecorder API is not supported in this browser')
      setBrowserCompatible(false)
      return
    }

    // Check for getUserMedia support
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.error('getUserMedia is not supported in this browser')
      setBrowserCompatible(false)
      return
    }

    // Test the MediaRecorder with dummy stream
    try {
      const dummyStream = new MediaStream()
      const recorder = new MediaRecorder(dummyStream)
      console.log('MediaRecorder initialized successfully:', recorder)
    } catch (e) {
      console.error('Error initializing MediaRecorder:', e)
      setBrowserCompatible(false)
    }

    // We now use askPermissionOnMount and the camera initialization useEffect instead
    // No need to request permissions here anymore
  }, [])

  // Media recorder options
  const {
    status,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearBlobUrl,
    mediaBlobUrl,
    previewStream,
    error,
  } = useReactMediaRecorder({
    video: true,
    audio: true,
    blobPropertyBag: { type: 'video/mp4' },
    onStop: (blobUrl, blob) => {
      console.log('onStop callback triggered with blob size:', blob?.size)
      // Don't automatically process recording - just make note that it's ready
      console.log('Recording stopped and ready for saving')
      toast.success('Recording ready. Click "Save Recording" to continue.', { duration: 3000 })
    },
    onStart: () => {
      console.log('Recording started callback')
      // Reset duration when recording starts
      setRecordingDuration(0)
    },
    askPermissionOnMount: true,
  })

  const [mediaConfig, setMediaConfig] = useState({
    isAudioEnabled: true,
    isVideoEnabled: true,
  })

  // Initialize camera on component mount
  useEffect(() => {
    let currentStream: MediaStream | null = null

    const initCamera = async () => {
      try {
        console.log('Initializing camera preview')
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true,
        })

        // Store in local variable for cleanup
        currentStream = stream
        setCameraStream(stream)

        // Connect to video preview
        if (videoPreviewRef.current) {
          videoPreviewRef.current.srcObject = stream
          console.log('Connected camera preview to video element')
        }
      } catch (err) {
        console.error('Error initializing camera preview:', err)
        toast.error('Unable to access camera. Please check your permissions.')
      }
    }

    initCamera()

    // Clean up the camera stream when component unmounts
    return () => {
      if (currentStream) {
        console.log('Cleaning up camera stream')
        currentStream.getTracks().forEach((track) => {
          track.stop()
          console.log(`Stopped track: ${track.kind}`)
        })
      }
    }
  }, [])

  // Add console logging to see what's happening with the status and streams
  useEffect(() => {
    console.log('Recording status changed:', status, 'at time:', new Date().toISOString())
    console.log('Preview stream available:', !!previewStream)

    // When recording starts, use the recording stream instead of the camera preview
    if (previewStream && videoPreviewRef.current && status === 'recording') {
      videoPreviewRef.current.srcObject = previewStream
      console.log('Connected recording stream to video element')
    }

    // Debug the "stopped" state which should enable the Save button
    if (status === 'stopped') {
      console.log('Recording has stopped - Save button should be enabled now')
    }
  }, [previewStream, status])

  useEffect(() => {
    // Reset and clean up on unmount
    return () => {
      if (timerInterval) clearInterval(timerInterval)
      if (mediaBlobUrl) clearBlobUrl()
    }
  }, [clearBlobUrl, mediaBlobUrl, timerInterval])

  // Start timer when recording begins
  useEffect(() => {
    console.log('Status changed to:', status)

    if (status === 'recording') {
      console.log('Starting recording timer')
      // Clear any existing interval first
      if (timerInterval) {
        clearInterval(timerInterval)
      }

      const interval = setInterval(() => {
        console.log('Timer tick')
        setRecordingDuration((prev) => {
          console.log('Current duration:', prev)
          return prev + 1
        })
      }, 1000)

      setTimerInterval(interval)
    } else if (status === 'stopped') {
      console.log('Recording stopped, duration:', recordingDuration)
      // When stopped, keep the duration but clear the interval
      if (timerInterval) {
        clearInterval(timerInterval)
        setTimerInterval(null)
      }
    } else if (status !== 'paused') {
      // For any other states except paused, clear the timer
      if (timerInterval) {
        clearInterval(timerInterval)
        setTimerInterval(null)
      }

      if (status === 'idle') {
        setRecordingDuration(0)
      }
    }

    // Make sure we clean up the interval when the component unmounts
    return () => {
      if (timerInterval) {
        console.log('Cleaning up timer interval')
        clearInterval(timerInterval)
      }
    }
  }, [status])

  const toggleAudio = () => {
    console.log('Toggle audio clicked')
    setMediaConfig((prev) => ({
      ...prev,
      isAudioEnabled: !prev.isAudioEnabled,
    }))

    // Apply the change to the current stream if recording
    if (previewStream) {
      previewStream.getAudioTracks().forEach((track) => {
        track.enabled = !mediaConfig.isAudioEnabled
      })
    }
  }

  const toggleVideo = () => {
    console.log('Toggle video clicked')
    setMediaConfig((prev) => ({
      ...prev,
      isVideoEnabled: !prev.isVideoEnabled,
    }))

    // Apply the change to the current stream if recording
    if (previewStream) {
      previewStream.getVideoTracks().forEach((track) => {
        track.enabled = !mediaConfig.isVideoEnabled
      })
    }
  }

  const formatTime = (seconds: number): string => {
    const hrs = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60

    return `${hrs > 0 ? `${hrs}:` : ''}${mins < 10 ? '0' : ''}${mins}:${secs < 10 ? '0' : ''}${secs}`
  }

  // Add console logging on error
  useEffect(() => {
    if (error) {
      console.error('Media recorder error:', error)
    }
  }, [error])

  const handleRecordingComplete = async (blob: Blob) => {
    console.log('handleRecordingComplete called with blob:', blob?.size)
    if (!blob) {
      toast.error('Recording failed. Please try again.')
      return
    }

    try {
      // Create a File object from the Blob
      const file = new File([blob], `${meetingTitle.trim() || 'Recording'}.mp4`, {
        type: 'video/mp4',
        lastModified: Date.now(),
      })

      // Get the duration of the recording
      const videoElement = document.createElement('video')
      videoElement.src = URL.createObjectURL(blob)

      videoElement.onloadedmetadata = () => {
        const duration = videoElement.duration

        // Add duration property to the file object to match the expected structure
        const fileWithDuration = Object.assign(file, {
          duration,
          fileName: `${meetingTitle.trim() || 'Recording'}.mp4`,
          size: blob.size,
        })

        setRecordedFile(fileWithDuration)
        // Upload the file directly instead of using ImportFiles
        handleUploadRecording(fileWithDuration)
      }
    } catch (error) {
      console.error('Error processing recording:', error)
      toast.error('Error processing recording. Please try again.')
    }
  }

  // New function to handle file upload directly
  const handleUploadRecording = async (file: File & { duration?: number; fileName?: string }) => {
    if (!file || !file.duration || !projectId) {
      toast.error('Invalid recording data.')
      return
    }

    setIsProcessing(true)

    try {
      // Create a FormData object to send the file directly
      const formData = new FormData()
      formData.append('file', file)
      formData.append('projectId', projectId)
      formData.append('fileName', file.fileName || file.name)
      formData.append('duration', String(file.duration))

      // Call the backend endpoint for recording uploads
      const response = await fetch(`${import.meta.env.VITE_SERVER_URL}/api/uploadRecording`, {
        method: 'POST',
        headers: {
          authorization: localStorage.getItem('access_token') || '',
          signinmethod: localStorage.getItem('signin_method') || '',
        },
        body: formData,
      })

      if (!response.ok) {
        const errorData = await response.json()
        toast.error(errorData.msg || 'Error uploading recording')
        setIsProcessing(false)
        return
      }

      const result = await response.json()

      if (result.error) {
        toast.error(result.error.msg || 'Error processing recording')
        setIsProcessing(false)
        return
      }

      toast.success("Recording uploaded! We'll let you know when the notes are ready.", {
        duration: 3000,
      })

      // Navigate back to project page after a delay
      setTimeout(() => {
        navigate(`/project/${projectId}`)
      }, 3000)
    } catch (error) {
      console.error('Error uploading recording:', error)
      toast.error('An error occurred while uploading your recording. Please try again.')
      setIsProcessing(false)
    }
  }

  const handleSaveRecording = async () => {
    console.log('Save recording clicked, status:', status, 'duration:', recordingDuration)

    // Only need to stop recording if it's active
    if (status === 'recording' || status === 'paused') {
      stopRecording()
      return // We'll let the onStop callback handle the upload
    }

    // If we already have a recorded file and status is stopped, process it directly
    if (status === 'stopped' && mediaBlobUrl) {
      console.log('Processing saved recording with mediaBlobUrl:', mediaBlobUrl)

      try {
        // Fetch the blob from the mediaBlobUrl
        const response = await fetch(mediaBlobUrl)
        const blob = await response.blob()

        if (!blob || blob.size === 0) {
          console.error('Failed to get blob from mediaBlobUrl')
          toast.error('Could not retrieve recording data')
          return
        }

        console.log('Successfully retrieved blob with size:', blob.size)

        // Create a File object from the Blob
        const file = new File([blob], `${meetingTitle.trim() || 'Recording'}.mp4`, {
          type: blob.type || 'video/mp4',
          lastModified: Date.now(),
        })

        // Add duration property to the file object
        const fileWithDuration = Object.assign(file, {
          duration: recordingDuration,
          fileName: `${meetingTitle.trim() || 'Recording'}.mp4`,
          size: blob.size,
        })

        setRecordedFile(fileWithDuration)

        // Upload the file
        handleUploadRecording(fileWithDuration)
      } catch (error) {
        console.error('Error processing saved recording:', error)
        toast.error('Error processing recording. Please try again.')
      }
    } else {
      console.warn('Cannot save recording - no media available or wrong state:', status)
      toast.error('No recording available to save')
    }
  }

  const handleClose = () => {
    setShowImportDialog(false)
    // Navigate back to project page if a recording was completed
    if (projectId && recordedFile) {
      navigate(`/project/${projectId}`)
    }
  }

  // Initialize the fallback recording method
  const initializeFallbackRecorder = async () => {
    if (fallbackRecorder) return

    try {
      console.log('Initializing fallback recorder')
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true,
      })

      setFallbackStream(mediaStream)

      // Connect to video preview
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = mediaStream
      }

      // Create recorder
      const recorder = new MediaRecorder(mediaStream, {
        mimeType: 'video/webm;codecs=vp9',
      })

      // Set up data handling
      setFallbackChunks([])
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          setFallbackChunks((prev) => [...prev, event.data])
        }
      }

      // Handle recording stop
      recorder.onstop = () => {
        const blob = new Blob(fallbackChunks, { type: 'video/webm' })
        handleRecordingComplete(blob)

        // Clean up
        mediaStream.getTracks().forEach((track) => track.stop())
        setFallbackStream(null)
      }

      setFallbackRecorder(recorder)
      console.log('Fallback recorder initialized successfully')
      setUsingFallback(true)
    } catch (err) {
      console.error('Error initializing fallback recorder:', err)
      toast.error('Failed to initialize recording. Please check your browser permissions.')
    }
  }

  const startFallbackRecording = () => {
    if (!fallbackRecorder) {
      initializeFallbackRecorder()
      return
    }

    try {
      fallbackRecorder.start(1000) // Collect data every second
      console.log('Fallback recording started')
    } catch (err) {
      console.error('Error starting fallback recording:', err)
    }
  }

  const stopFallbackRecording = () => {
    if (!fallbackRecorder) return

    try {
      fallbackRecorder.stop()
      console.log('Fallback recording stopped')
    } catch (err) {
      console.error('Error stopping fallback recording:', err)
    }
  }

  // Add a button to try fallback recording if react-media-recorder isn't working
  const tryFallbackRecording = () => {
    console.log('Trying fallback recording method')
    setUsingFallback(true)
    initializeFallbackRecorder()
  }

  // Display browser compatibility error
  if (!browserCompatible) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md text-center">
          <AlertTriangle className="text-amber-500 w-12 h-12 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-red-600 mb-4">Browser Not Supported</h1>
          <p className="mb-6">
            Your browser doesn't support video recording. Please try using a modern browser like Chrome, Firefox, or
            Edge.
          </p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    )
  }

  // Display error if permissions denied
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Recording Error</h1>
          <p className="mb-6">
            {typeof error === 'string'
              ? error
              : 'Could not access your camera or microphone. Please check your permissions and try again.'}
          </p>
          <Button onClick={() => navigate(-1)}>Go Back</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm p-4 border-b flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-500">
            Cancel
          </Button>
          <input
            type="text"
            className="text-black bg-white text-[26px] font-semibold font-['Manrope'] leading-normal border-none focus:ring-0 outline-none"
            value={meetingTitle}
            onChange={(e) => setMeetingTitle(e.target.value)}
            placeholder="Meeting Title"
          />
        </div>
        <div>
          <Button
            variant="default"
            className={`${
              status === 'stopped' ? 'bg-green-500 hover:bg-green-600 animate-pulse' : 'bg-pink hover:bg-pink/90'
            } text-white transition-colors`}
            onClick={handleSaveRecording}
            disabled={status !== 'stopped'}
          >
            <Save className="w-4 h-4 mr-2" />
            {status === 'stopped' ? 'Save Recording' : 'Recording...'}
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        {/* Video preview */}
        <div className="w-full max-w-3xl bg-black rounded-lg overflow-hidden shadow-lg aspect-video relative mb-6">
          {!cameraStream && !previewStream ? (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-900">
              <p className="text-white text-xl">Initializing camera...</p>
            </div>
          ) : (
            <>
              <video ref={videoPreviewRef} autoPlay muted className="w-full h-full object-cover" />
              {/* Recording status overlay */}
              {status === 'recording' && (
                <div className="absolute top-4 left-4 flex items-center space-x-2 bg-black/50 rounded-full px-3 py-1">
                  <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
                  <span className="text-white text-sm font-medium">{formatTime(recordingDuration)}</span>
                </div>
              )}
              {status === 'paused' && (
                <div className="absolute top-4 left-4 flex items-center space-x-2 bg-black/50 rounded-full px-3 py-1">
                  <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                  <span className="text-white text-sm font-medium">{formatTime(recordingDuration)}</span>
                </div>
              )}
              {/* Recording stopped overlay */}
              {status === 'stopped' && mediaBlobUrl && (
                <div className="absolute top-4 left-4 flex items-center space-x-2 bg-green-500/70 rounded-full px-3 py-1">
                  <div className="w-3 h-3 rounded-full bg-white"></div>
                  <span className="text-white text-sm font-medium">
                    Ready to save ({formatTime(recordingDuration)})
                  </span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center space-x-6 mb-8">
          {/* Record/Stop button */}
          <Button
            variant="outline"
            className={`rounded-full p-3 h-12 w-12 ${
              status === 'recording' || status === 'paused'
                ? 'bg-black text-white border-black hover:bg-black/90 hover:border-black/90'
                : 'bg-pink text-white border-pink hover:bg-pink/90 hover:border-pink/90'
            }`}
            onClick={() => {
              if (status === 'recording' || status === 'paused') {
                console.log('Stop recording clicked. Current status:', status)
                // Important: we need to manually reset the recording duration when stopping
                stopRecording()
              } else {
                console.log('Start recording clicked. Current status:', status)
                // Reset recording duration when starting
                setRecordingDuration(0)
                startRecording()
              }
            }}
          >
            {status === 'recording' || status === 'paused' ? (
              <Square className="w-5 h-5" />
            ) : (
              <Circle className="w-5 h-5 fill-white" />
            )}
          </Button>

          {/* Audio toggle */}
          <Button variant="outline" className="rounded-full p-3 h-12 w-12" onClick={toggleAudio}>
            {mediaConfig.isAudioEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5 text-red-500" />}
          </Button>

          {/* Video toggle */}
          <Button variant="outline" className="rounded-full p-3 h-12 w-12" onClick={toggleVideo}>
            {mediaConfig.isVideoEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5 text-red-500" />}
          </Button>
        </div>

        {/* Instructions */}
        <div className="text-center max-w-lg">
          <p className="text-gray-500 text-sm">
            Use the Record button to start/stop recording. Toggle mic and camera as needed. Your recording will be
            processed automatically once completed.
          </p>
          <p className="text-gray-400 text-xs mt-2">
            Current status: {status} | Microphone: {mediaConfig.isAudioEnabled ? 'On' : 'Off'} | Camera:{' '}
            {mediaConfig.isVideoEnabled ? 'On' : 'Off'}
          </p>
        </div>
      </div>

      {/* Processing indicator */}
      {isProcessing && (
        <div
          style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1000 }}
          className="flex items-center justify-center bg-black/50"
        >
          <div className="bg-white p-8 rounded-lg shadow-lg text-center">
            <div className="animate-spin mb-4 w-12 h-12 border-4 border-pink border-t-transparent rounded-full mx-auto"></div>
            <h3 className="text-xl font-bold mb-2">Processing Recording</h3>
            <p className="text-gray-600">Please wait while we upload and process your recording.</p>
          </div>
        </div>
      )}
    </div>
  )
}

export default RecordingPage
